import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePassport } from '../../../hooks/usePassport';

const PassportEntryPage = ({ setPageIndex }) => {
	const API_URL = import.meta.env.VITE_API_URL;
	const queryClient = useQueryClient();
	const { passport, existPassport } = usePassport();
	const [form, setForm] = useState({ ch_id: "", name: "", grade: "" });
	const [formErrors, setFormErrors] = useState({ name: "", grade: "" });
	const [submitError, setSubmitError] = useState("");

	const { data } = useQuery({
		queryKey: ['member'],
		queryFn: async () => {
			const response = await fetch(`${API_URL}/bbs/ajax.get_member.php`);
			return response.json();
		}
	});
	const member = data?.data ?? null;

	const childrenWithoutPassport = member?.children?.filter((child) => !child.passportId) ?? [];
	const childrenWithPassport = member?.children?.filter((child) => !!child.passportId) ?? [];

	useEffect(() => {
		if (member) {
			setForm({ ch_id: "", name: member.memberChildName || "", grade: member.memberGrade || "" });
		}
	}, [member]);

	const mutation = useMutation({
		mutationFn: async (data: { name: string, grade: string }) => {
			const params = new URLSearchParams();
			Object.entries(data).forEach(([key, value]) => {
				params.append(key, value);
			});

			const response = await fetch(`${API_URL}/bbs/ajax.create_passport.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: params,
			});

			if (!response.ok) throw new Error("네트워크 오류");

			return response.json();
		},
		onSuccess: (response) => {
			if (!response.success) {
				switch (response.code) {
					case "UNAUTHORIZED":
					case "NOT_FOUND_MEMBER":
						setSubmitError("인증된 사용자가 아닙니다.");
						break;
					case "EXIST_PASSPORT":
						setSubmitError("이미 여권이 있습니다.");
						break;
					case "INVALID_REQUEST":
						setSubmitError("필수 필드값이 입력되지 않았습니다.");
						break;
					default:
						setSubmitError("알 수 없는 오류가 발생했습니다.");
						break;
				}
				return;
			}

			if (!response.data) {
				setSubmitError("알 수 없는 오류가 발생했습니다.");
				return;
			}

			if (response.data.code) {
				queryClient.invalidateQueries({ queryKey: ['passport'] });
				queryClient.invalidateQueries({ queryKey: ['member'] });
				setForm({ name: "", grade: "" });
				setPageIndex(2);
			}
		},
		onError: (error: any) => {
			setSubmitError(error.message);
		}
	});

	const validate = (name: string, value: string) => {
		let errorMsg = "";

		if (name === "name" && !value.trim()) {
			errorMsg = "이름을 선택해주세요.";
		}
		if (name === "grade" && !value.trim()) {
			errorMsg = "학년을 선택해주세요.";
		}

		return { value, error: errorMsg };
	};

	const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
	    const { name, value } = e.target;

	    if (name === "name") {
		    const selectedChild = childrenWithoutPassport.find((child) => child.id === value);
		    const resolvedName = selectedChild?.name ?? "";
		    setForm(prev => ({ ...prev, ch_id: value, name: resolvedName }));
		    setFormErrors(prev => ({ ...prev, name: resolvedName.trim() ? "" : "이름을 선택해주세요." }));
		    setSubmitError("");
		    return;
		} else {
	        setForm(prev => ({ ...prev, [name]: value }));
	    }

	    const result = validate(name, value);
	    setSubmitError("");
	    setFormErrors(prev => ({ ...prev, [name]: result.error }));
	};

	const handleSubmit = () => {
		const fields = ["name", "grade"];
		const newErrors: any = {};
		const newForm: any = { ...form };

		fields.forEach((field) => {
			const result = validate(field, form[field]);
			newErrors[field] = result.error;
			newForm[field] = result.value;
		});

		setFormErrors(newErrors);

		if (Object.values(newErrors).some((e) => e)) return;

		mutation.mutate(newForm);
	};

	return (
		<div className="bg-transparent md:bg-gradient-to-br md:from-blue-900 md:to-blue-600 rounded-none md:rounded-[20px] px-5 py-12 pb-24 md:px-8 md:py-16 md:pb-28 md:px-10 md:py-20 md:pb-32">
			<div className="w-[120px] h-[120px] bg-white/20 border-[3px] border-white rounded-full flex items-center justify-center text-[60px] mx-auto mb-8 mt-3">🌍</div>
			<h1 className="text-5xl font-black mb-4 text-white">AI 탐험 여권</h1>
			<p className="text-xl mb-8 opacity-90 text-white">ARTIFICIAL INTELLIGENCE PASSPORT</p>
			{childrenWithPassport.length > 0 && (
				<div className="max-w-[400px] mx-auto mb-6 flex flex-col gap-4">
					{childrenWithPassport.map((child, index) => (
						<div key={child.id ?? index} className="bg-white/20 backdrop-blur-md p-6 rounded-[20px] relative">
							<div className="absolute top-4 right-4 bg-white/20 px-3 py-1 rounded-full border-2 border-white font-bold text-white text-sm">
								LEVEL <span>{child.passport?.level ?? 1}</span>
							</div>
							<div className="text-2xl font-bold mb-1 text-white">
								{child.name}
							</div>
							<div className="text-sm mb-1 text-white">
								(초등 {child.grade}학년)
							</div>
							<div className="text-sm mt-4 mb-1 text-white">
								여권번호: {child.passport?.code}
							</div>
							<div className="text-sm opacity-75 text-white">
								발급일: {child.passport?.datetime?.split(' ')[0]}
							</div>
						</div>
					))}
				</div>
			)}
			{childrenWithoutPassport.length > 0 && (
				<div className="bg-white/20 backdrop-blur-md p-10 rounded-[20px] max-w-[400px] mx-auto">
					<p className="text-white text-sm font-semibold mb-4">새 여권 발급</p>
					<div className="relative mt-3">
						<select
							name="name"
							id="name"
							value={form.ch_id}
							onChange={handleChange}
							disabled={mutation.isPending}
							className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm appearance-none pr-8"
						>
							<option value="">이름을 선택해주세요</option>
							{childrenWithoutPassport.map((child, index) => (
								<option key={child.id ?? index} value={child.id}>
									{child.name}
								</option>
							))}
						</select>
						{formErrors.name && (
							<p className="text-red-500 text-xs mt-1 min-h-[16px]">
								{formErrors.name}
							</p>
						)}
						<i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
					</div>
					<div className="relative mt-3">
						<select
							name="grade"
							id="grade"
							value={form.grade}
							onChange={handleChange}
							disabled={mutation.isPending}
							className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm appearance-none pr-8"
						>
							<option value="">학년을 선택해주세요</option>
							<option value="1">초등 1학년</option>
							<option value="2">초등 2학년</option>
							<option value="3">초등 3학년</option>
							<option value="4">초등 4학년</option>
							<option value="5">초등 5학년</option>
							<option value="6">초등 6학년</option>
						</select>
						<i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
					</div>
					{formErrors.grade && (
						<p className="text-red-500 text-xs mt-1 min-h-[16px]">
							{formErrors.grade}
						</p>
					)}
					<button
						onClick={handleSubmit}
						disabled={mutation.isPending}
						className={`w-full px-8 py-4 rounded-full font-bold hover:bg-yellow-300 mt-3 transition
							${mutation.isPending ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-yellow-400 text-blue-900"}
						`}
					>
						{mutation.isPending ? "여권 만드는 중..." : "여행 시작하기 ✈️"}
					</button>
					{submitError && (
						<p className="text-red-500 text-xs mt-1 min-h-[16px]">
							{submitError}
						</p>
					)}
				</div>
			)}
		</div>
	);
}

export default PassportEntryPage;